/* eslint-disable no-param-reassign */
import { action, computed, makeAutoObservable, observable, toJS } from "mobx";
// @ts-ignore
import { notify } from "@microui/react-ui";
import { isEmpty, isFunction } from "coreutils-js";
// import UserAPI from "api/user";
import ChannelAPI from "api/channel";
import PermissionAPI from "api/permission";
import PermissionGroupAPI from "api/permissiongroup";
import PublicAPI from "api/public";
import RoleAPI from "api/role";
import UserAPI from "api/user";
import CommonStatus from "constants/types/common-status";
import { compareArrays } from "libs/utils";
import RootStore from "stores/RootStore";
import RoleType from "constants/types/role-type";

const defaultAccountFilter = {
  keyword: "",
  pageIndex: 1,
  pageSize: 20,
  status: CommonStatus.ACTIVED,
};

class AddMemberStore {
  permissionApi: PermissionAPI;

  permissionGroupApi: PermissionGroupAPI;

  userApi: UserAPI;

  rootStore: RootStore;

  channelAPI: ChannelAPI;

  roleApi: RoleAPI;

  publicApi: PublicAPI;

  constructor({ refreshToken, applicationId, channelId, accountId, dataOrganization, defaultAccount }) {
    makeAutoObservable(this);
    this.roleApi = new RoleAPI({ refreshToken });
    this.permissionApi = new PermissionAPI({ refreshToken, });
    this.permissionGroupApi = new PermissionGroupAPI({ refreshToken, });
    this.userApi = new UserAPI({ refreshToken });
    this.channelAPI = new ChannelAPI({ refreshToken });
    this.publicApi = new PublicAPI({ refreshToken });

    this.applicationId = applicationId;
    this.dataOrganization = dataOrganization;
    this.channelId = channelId;
    this.accountId = accountId;
    this.defaultAccount = defaultAccount;
  }

  @observable applicationId = null;
  @observable dataOrganization = null;
  @observable channelId = null;
  @observable accountId = null;
  @observable defaultAccount = null;

  @observable roleList = [];
  @observable roleScope = [];
  @observable selectedRoleList: any[] = [];
  @observable permissionsData = [];
  @observable step = 0; // 0 - member, 1 - role
  @observable typeAccount = "invite"; // create, invite
  @observable accountData = [];
  @observable accountTotal = 0;
  @observable accountFilters = { ...defaultAccountFilter };
  @observable selectedAccount: any = null;
  @observable selectedPermission = [];
  @observable formCreatorAccount = null;
  @observable formMode = 'create'; // create, update

  //Loading
  @observable loadingMember = false;
  @observable creatingAccount = false;
  @observable saving = false;
  @observable loading = false;

  @computed get enableRoleScope() {
    return this.selectedRoleList.findIndex(role => role.type == RoleType.ALL) == -1;
  }

  @computed get selectedRoleKeys() {
    return this.selectedRoleList.map((e) => e.id);
  }

  @computed get dataPermissions() {
    const permissionsData = [...toJS(this.permissionsData)];
    let data = [];
    data = permissionsData.filter((item) => {
      const childList = item.child.filter((child) => this.mergedPermissions?.includes(child.code));
      item.child = childList;
      return childList.length > 0;
    });
    return data;
  }

  @computed get mergedPermissions() {
    const set = new Set();
    this.selectedRoleList.forEach(role => {
      role.permissions.forEach(code => {
        set.add(code);
      })
    })
    return Array.from(set).filter(e => !isEmpty(e))
  }

  @action setValue = (data: Partial<this>, callback = null) => {
    if (isFunction(data)) {
      // @ts-ignore
      Object.assign(this, data(this));
    } else {
      Object.assign(this, data);
    }

    if (callback) {
      callback();
    }
  };

  @action
  getPermissionByRole(id) {
    return this.roleList
      .find((val) => val.id === id)
      .permissions?.filter((e) => !isEmpty(e));
  }

  @action resetStore = () => {
    this.selectedRoleList = [];
    this.accountData = [];
    this.accountTotal = 0;
    this.accountFilters = { ...defaultAccountFilter };
    this.permissionsData = [];
    this.selectedPermission = [];
    this.roleList = [];
    this.typeAccount = "invite";
    this.step = 0;
    this.selectedAccount = null;
    this.loadingMember = false;
  };

  @action handleSelectPermission = (perm) => {
    this.selectedPermission = perm;
  };

  // Member
  @action
  getMemberInfo(channelId, accountId, data) {
    return new Promise((resolve, reject) => {
      this.loadingMember = true;
      this.channelAPI
        .getMember(channelId, accountId, data)
        .then(({ data }) => {
          this.selectedAccount = data.accountInfo;
          const codes = new Set();
          data.permissions.filter(v => v != '*' && !!v).forEach(code => {
            codes.add(code);
          });
          this.selectedPermission = Array.from(codes);
          this.roleScope = data.scopes.filter(v => v != '*' && !!v);
          this.selectedRoleList = data.roles.map(role => {
            role.permissionSelected = role.permissions ?? [];
            return role;
          });
          // this.editRole(data.roles[0]);
          resolve(data);
        })
        .catch((err) => reject(err))
        .finally(() => (this.loadingMember = false));
    });
  }

  // Account
  @action getDataAccount(reset = false) {
    this.loadingMember = true;

    if (reset) {
      this.accountFilters.pageIndex = 1;
    }

    this.userApi
      .searchUsers(this.accountFilters)
      .then(({ data }) => {
        if (reset) {
          this.accountData = data.data;
        } else {
          this.accountData.push(...data.data);
        }

        this.accountTotal = data.total;
      })
      .finally(() => {
        this.loadingMember = false;
      });
  }

  @action
  registerAccount(payload: Object) {
    return new Promise((resolve, reject) => {
      this.creatingAccount = true;
      this.publicApi.register(payload)
        .then((res) => {
          resolve(res);
          notify({
            type: "primary",
            color: "success",
            title: "Tạo tài khoản thành công",
          });
        })
        .catch((err) => {
          reject(err);
          notify({
            type: "primary",
            color: "error",
            message: err.message,
            title: "Tạo tài khoản không thành công",
          });
        })
        .finally(() => {
          this.creatingAccount = false;
        });
    });
  }

  // Quyền
  @action getDataPermission(applicationId, channelId, roleId) {
    return new Promise((resolve, reject) => {
      this.loading = true;
      const promise = [
        this.permissionApi.getPermission(applicationId),
        this.permissionGroupApi.getPermissionGroup(applicationId),
        roleId && this.roleApi.getRole(roleId),
      ].filter(Boolean);

      Promise.all(promise)
        .then((results) => {
          const permissions = results[0]?.data?.data ?? [];
          const permissionsGroup = results[1]?.data?.data ?? [];
          const roleInfo = results[2]?.data ?? {};
          this.permissionsData = permissionsGroup.map((e) => ({
            ...e,
            child: permissions.filter((p) => p.groupId == e.id),
          }));

          let rolePermissions = [];

          if (roleInfo?.permissions === "*") {
            rolePermissions = permissions.map((i) => i.code);
          } else {
            rolePermissions = roleInfo?.permissions ?? [];
          }

          resolve({ roleInfo, rolePermissions });
        })
        .finally(() => {
          this.loading = false;
        });
    });
  }

  @action submitData = (applicationId, channelId, accountId) => {
    return new Promise((resolve, reject) => {
      this.saving = true;
      /** bắt đầu lọc những quyền không có trong danh sách role đã chọn */
      const listAllPermissionInRolesSelected:string[] = this.selectedRoleList.reduce((prev, current) => {
        return ([...prev,...current.permissions])
      }, []).filter(e=>!!e);
      
      /** code quyền đã lọc */
      const permissionCodes = this.selectedPermission.filter(code =>  listAllPermissionInRolesSelected.includes(code));
      /** kết thúc lọc những quyền không có trong danh sách role đã chọn */

      if (this.selectedRoleList.length === 0) {
        notify.error("Bạn phải chọn ít nhất 1 vai trò");
        this.saving = false;
        return reject(new Error("Bạn phải chọn ít nhất 1 vai trò"));
      }

      const data = {
        roleIds: this.selectedRoleList.filter(v => v != '*' && !!v).map((e) => e.id).join(';'),
        permissions: permissionCodes.filter(v => v != '*' && !!v).join(';'),
        scopes: this.enableRoleScope ? this.roleScope.filter(v => v != '*' && !!v).join(';') : '*'
      };

      if (this.formMode === 'update') {
        this.channelAPI
          .updateMember(channelId, accountId, {
            applicationId,
            ...data,
          })
          .then((res) => resolve({ ...data, id: res.data }))
          .catch((err) => reject(err))
          .finally(() => (this.saving = false));
      } else {
        this.channelAPI
          .addMember(channelId, {
            applicationId,
            accountId,
            ...data,
          })
          .then((res) => resolve({ ...data, id: res.data }))
          .catch((err) => reject(err))
          .finally(() => (this.saving = false));
      }

    });
  };

  // Role
  @action getDataRole(applicationId, channelId) {
    this.roleApi
      .searchRole({
        pageSize: 9999,
        pageIndex: 1,
        status: CommonStatus.ACTIVED,
        applicationId,
        channelId,
      })
      .then(({ data }) => {
        this.roleList = data?.data?.map((val) => ({
          ...val,
          value: val.id,
          label: val.name,
        }));
      });
  }

  @action selectRole(role) {
    const isIncludes = this.selectedRoleList.map((e) => e.id).includes(role.id);

    if (!isIncludes) {
      this.selectedRoleList.push(role);

      if (!role?.permissionSelected) {
        this.selectedRoleList.forEach((e) => {
          if (e.id === role.id) {
            e.permissionSelected = role.permissions ?? [];
          }
        });
      }
    } else {
      this.selectedRoleList = this.selectedRoleList.filter(
        (r) => r.id !== role.id
      );

    }
  }

}

export default AddMemberStore;
